import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledSelectField, ControlledTextField } from 'components/Form';
import { getStageLabel } from 'components/ProjectStage';
import { getStatusLabel } from 'components/ProjectStatus';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { ProjectContractTypeEnum, ProjectStageEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useIsUniqProjectName } from 'hooks/useIsUniqProjectName';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { getContractTypeLabel } from 'types/Contract';
import { Button, cnMixSpace, Grid, GridItem } from 'uikit';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useIsUniqueProjectCode } from '../hooks/useIsUniqueProjectCode';

import { createProjectGql } from './projectCreateQueries';

import Styles from './ProjectCreateForm.module.css';

const selectValueById = <T extends { id: T['id'] }>(items: T[], id: T['id']) => items.find((item) => item.id === id);

export const ProjectCreateForm = ({ defaultValues }: any) => {
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();
    const { project: projectRules } = useAccess();
    const {
        control,
        handleSubmit,
        setError,
        clearErrors,
        setValue,
        formState: { dirtyFields, errors },
    } = useForm<any>({
        criteriaMode: 'all',
        defaultValues,
    });

    const projectName = useWatch({ control, name: 'name' });
    const projectMethodology = useWatch({ control, name: 'methodology' });
    const projectStage = useWatch({ control, name: 'stage' });
    const uerProjectCode = useWatch({ control, name: 'code' });

    const { dataIsUniqProjectName, statusIsUniqProjectName, makeResetIsUniqProjectName } =
        useIsUniqProjectName(projectName);

    useEffect(() => {
        if (projectName && statusIsUniqProjectName === RequestStatus.SUCCESS && !dataIsUniqProjectName?.result) {
            setError('name', { message: 'Проект с таким именем уже существует' });
        } else {
            clearErrors('name');
        }
    }, [clearErrors, dataIsUniqProjectName?.result, projectName, setError, statusIsUniqProjectName]);

    const { isUniqueProjectCode } = useIsUniqueProjectCode({
        projectCode: uerProjectCode,
        isProjectCodeDirty: dirtyFields?.code,
    });

    useEffect(() => {
        if (uerProjectCode && !isUniqueProjectCode) {
            setError('code', { message: 'Проект с таким кодом уже существует' });
        } else {
            clearErrors('code');
        }
    }, [clearErrors, isUniqueProjectCode, setError, uerProjectCode]);

    const dispatch = useDispatch();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const navigate = useNavigate();

    const [createProjectData, createProjectStatus, createProjectError, createProject] = useRequest({
        requestId: 'createProject',
        query: createProjectGql,
        withEffect: false,
    });

    useEffect(() => {
        if (isSubmitted && createProjectStatus === RequestStatus.SUCCESS && createProjectData?.createProject?.result) {
            const { uniqueId, methodology, name, contractType, subsidiary, stage } =
                createProjectData?.createProject?.data || {};

            if (methodology && uniqueId) {
                navigate(`/projects/${methodology.toLowerCase()}/${uniqueId}/`);

                sendGpnAnalyticsEvent({
                    componentId: 'rim_create project backend_saved',
                    methodology,
                    projectId: uniqueId,
                    projectName: name,
                    projectType: contractType,
                    projectDO: subsidiary?.name,
                    projectStage: stage,
                });
            }
        }
    }, [createProjectData, isSubmitted, navigate, createProjectStatus, sendGpnAnalyticsEvent]);

    const handleSave = useCallback(() => {
        sendGpnAnalyticsEvent({
            componentId: 'rim_create project page create project button_pressed',
        });
    }, [sendGpnAnalyticsEvent]);

    const handleCancel = useCallback(() => {
        navigate('/projects');
    }, [navigate]);

    const onCloseSaveErrorModal = useCallback(() => {
        setIsSubmitted(false);
    }, [setIsSubmitted]);

    const onSaveErrorReport = useCallback(() => {
        ExportCSVReport(createProjectData?.createProject?.validation);
    }, [createProjectData?.createProject?.validation]);

    const methodologySelectItems = useMemo(
        () => [
            { label: 'УЕР', id: 'UER', disabled: false },
            { label: 'БИМ', id: 'BIM', disabled: false },
        ],
        [],
    );

    const contractTypeSelectItems = useMemo(
        () => [
            { id: ProjectContractTypeEnum.Frame, label: getContractTypeLabel(ProjectContractTypeEnum.Frame) },
            { id: ProjectContractTypeEnum.Normal, label: getContractTypeLabel(ProjectContractTypeEnum.Normal) },
        ],
        [],
    );

    const stageSelectItems = useMemo(
        () => [
            {
                label: getStageLabel(ProjectStageEnum.Calculation),
                id: ProjectStageEnum.Calculation,
            },
            {
                label: getStageLabel(ProjectStageEnum.Implementation),
                id: ProjectStageEnum.Implementation,
            },
        ],
        [],
    );

    const statusSelectItems = useMemo(
        () => [
            {
                label: getStatusLabel(ProjectStatusEnum.InImplementation),
                id: ProjectStatusEnum.InImplementation,
            },
            {
                label: getStatusLabel(ProjectStatusEnum.InCalculation),
                id: ProjectStatusEnum.InCalculation,
            },
        ],
        [],
    );

    const isNotBimProject = projectMethodology?.id !== selectValueById(methodologySelectItems, 'BIM')?.id;

    const onSubmit = useCallback(
        (data) => {
            dispatch(
                createProject({
                    ...data,
                    methodology: data.methodology?.id,
                    stage: data.stage?.id,
                    status: data.status?.id,
                    contractType: data.contractType?.id,
                    ...(data?.code && { code: data?.code }),
                }),
            );
            setIsSubmitted(true);
        },
        [createProject, dispatch],
    );

    useEffect(() => {
        if (!projectStage?.id) {
            setValue('status', undefined);
        } else if (projectStage?.id === ProjectStageEnum.Calculation) {
            setValue('status', selectValueById(statusSelectItems, ProjectStatusEnum.InCalculation));
        } else {
            setValue('status', selectValueById(statusSelectItems, ProjectStatusEnum.InImplementation));
        }
    }, [projectStage, setValue, statusSelectItems]);

    useEffect(() => {
        if (isNotBimProject) {
            setValue('contractType', selectValueById(contractTypeSelectItems, ProjectContractTypeEnum.Frame));
        }
    }, [isNotBimProject, contractTypeSelectItems, setValue]);

    useEffect(
        () => () => {
            makeResetIsUniqProjectName(null);
        },
        [makeResetIsUniqProjectName],
    );

    const createBtnDisabled = useMemo(
        () => !projectRules.canCreate || !!Object.keys(errors).length,
        [errors, projectRules.canCreate],
    );

    return (
        <div className={Styles.projectCreateForm}>
            <div className={Styles.projectCreateFormSplitter} />
            <form onSubmit={handleSubmit(onSubmit)} className={Styles.projectCreateFormBody}>
                <GridItem style={{ display: 'flex', marginBottom: '30px' }}>
                    <Button
                        id="rim_create project page create project button_pressed"
                        label="Создать проект"
                        onClick={handleSave}
                        loading={createProjectStatus === RequestStatus.FETCHING}
                        disabled={createBtnDisabled}
                        type="submit"
                    />
                    <Button
                        label="Отменить"
                        view="ghost"
                        onClick={handleCancel}
                        disabled={createProjectStatus === RequestStatus.FETCHING}
                        className={cnMixSpace({ mL: 's' })}
                    />
                </GridItem>
                <h3 className={Styles.projectCreateFormTitle}>Основная информация</h3>
                <Grid rowGap="xs" cols="1">
                    <GridItem>
                        <ControlledTextField
                            name="name"
                            label="Наименование"
                            control={control}
                            type="text"
                            placeholder="Наименование проекта"
                            required
                            rules={{
                                required: true,
                                maxLength: {
                                    value: 255,
                                    message: 'не более 255 символов',
                                },
                            }}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="methodology"
                            label="Методология расчета"
                            control={control}
                            placeholder="Методология расчета"
                            required
                            items={methodologySelectItems}
                            rules={{
                                required: true,
                            }}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="stage"
                            label="Этап"
                            control={control}
                            placeholder="Этап"
                            required
                            items={stageSelectItems}
                            rules={{
                                required: true,
                            }}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="status"
                            label="Статус"
                            control={control}
                            placeholder=""
                            required
                            items={statusSelectItems}
                            disabled
                        />
                    </GridItem>
                    {projectMethodology?.id && (
                        <GridItem>
                            <ControlledTextField
                                name="code"
                                label="Код проекта"
                                control={control}
                                rules={{
                                    maxLength: {
                                        value: 255,
                                        message: 'не более 255 символов',
                                    },
                                }}
                            />
                        </GridItem>
                    )}
                    <GridItem>
                        <ControlledTextField
                            name="subsidiary"
                            label="Дочернее общество"
                            control={control}
                            placeholder="Дочернее общество"
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="contractType"
                            label="Тип договора"
                            control={control}
                            placeholder="Тип договора"
                            items={contractTypeSelectItems}
                            disabled={isNotBimProject}
                            rules={{
                                required: true,
                            }}
                        />
                    </GridItem>
                </Grid>
            </form>
            <ErrorHandlingModal
                titleText="Создание проекта"
                isSubmitted={isSubmitted}
                statusRequest={createProjectStatus}
                dataRequest={createProjectData?.createProject}
                errorsRequest={createProjectError}
                handleCreateReport={onSaveErrorReport}
                handleCloseModalError={onCloseSaveErrorModal}
            />
        </div>
    );
};

ProjectCreateForm.displayName = 'ProjectCreateForm';
